import { useEffect, useRef } from "react";

/**
 * Report something once per occurrence, not once per render.
 *
 * Every funnel event needs the same non-obvious answer to "when", and before
 * this hook existed each surface answered it separately: four call sites, three
 * dialects (a latch cleared on leaving, a latch never cleared, and a latch keyed
 * on a tally), and one bug already shipped from getting it wrong - the quote-diff
 * banner re-fired `quote_diff_viewed` on every sidebar filter change until it was
 * re-pointed at the unfiltered carts.
 *
 * The rule is two sentences:
 *
 * 1. **`report` runs once for each distinct non-null `key`, and a null key
 *    re-arms it.** The key names the *occurrence* - the thing that, when it
 *    changes, means "this is a new one".
 * 2. **`ready: false` holds:** nothing reports, and nothing re-arms, until the
 *    caller has something worth reporting.
 *
 * The three dialects are then three choices of key:
 *
 * - a constant key - report once, for as long as the caller keeps passing it
 * - a key derived from the data (a tally, an id) - report again when it changes
 * - a key that goes null when the surface closes - report again when it reopens
 *
 * **Keep readiness out of the key.** It is the one mistake this interface
 * invites, and it has already been made once: keying the comparison matrix on
 * `isMatrixView && carts.length > 0` meant filtering the grid down to nothing
 * re-armed it, so clearing the filter fired `comparison_matrix_viewed` a second
 * time - the very re-fire the quote-diff banner taught us to avoid. "Not ready
 * yet" and "closed, so the next one is a new one" are different facts and a
 * single nullable key cannot carry both. `ready` carries the first, the key
 * carries the second.
 *
 * `ready` wins over the key: while it is false the latch is frozen as-is,
 * neither fired nor cleared. Callers whose surface can close *while* unready
 * would need the opposite, and none exist - the cart's empty-filter state does
 * not render the tabs that would let you leave it, and a search with no query
 * has nothing to key on either way.
 *
 * `report` is read through a ref, so callers pass an inline closure without
 * wrapping it in `useCallback` and without it appearing in a dependency array.
 * Only `key` and `ready` decide when it runs.
 *
 * It receives the key it fired for. That is always the non-null one, so a caller
 * whose key *is* the reported value (the search query) can take it from here
 * rather than re-narrowing a nullable prop at the call site.
 *
 * StrictMode-safe: development runs effects setup -> cleanup -> setup while
 * preserving refs, and the second setup sees the key already reported and does
 * nothing. This is the single place that hazard is handled.
 *
 * @param key - names the occurrence; null re-arms for the next one
 * @param report - run once per occurrence with the key it fired for, read live
 *   so it can close over props
 * @param ready - false holds the latch untouched (default true)
 *
 * @example
 * ```tsx
 * // Once per opening of the matrix tab - the tab is the occurrence - and not
 * // until there is something in it. Filtering the grid empty holds; it does
 * // not re-arm.
 * useReportOnce(isMatrixView ? "matrix" : null, () => {
 *   analytics.comparisonMatrixViewed({ line_count, supplier_count });
 * }, carts.length > 0);
 * ```
 */
export function useReportOnce(
  key: string | null,
  report: (key: string) => void,
  ready = true
): void {
  const reportRef = useRef(report);
  reportRef.current = report;

  const reportedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    // Checked before the key: an unready surface has not closed, it just has
    // nothing to say yet, so the latch must survive it untouched.
    if (!ready) {
      return;
    }
    if (key === null) {
      reportedKeyRef.current = null;
      return;
    }
    if (reportedKeyRef.current === key) {
      return;
    }
    reportedKeyRef.current = key;
    reportRef.current(key);
  }, [key, ready]);
}
